import { useMemo, useState, useRef } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Chip, FilterRow, Icon, Pane, PaneHead } from '@/components/kit';
import { useShellChrome, useNarrowViewport } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuthStore } from '@/stores/useAuthStore';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import { AutoTopUpPane } from './AutoTopUpPane';
import { useWallet, walletRunwayWeeks, type WalletSummary } from '../lib/useManagerData';
import { SettingsRail } from './SettingsRail';
import { TextEditor } from './editors';
import { useMessagePrices, usePaymentProviders, useTopUp } from './useWallet';
import { WalletAccess } from './WalletAccess';
import { WalletLedger } from './WalletLedger';
import { exportCsv } from '@/pages/roles/lib/exportCsv';
import { WalletEmpty } from './WalletEmpty';

/**
 * Clinic manager · Settings ▸ Message wallet — `g_wallet.dc.html` (the hub)
 * and `g_wtop.dc.html` (the Top up door docked on it).
 *
 * ONE SCREEN, ONE DOOR OPEN. The two boards are the same hub with a sheet
 * docked, which is why they are one component and one route: `?door=topup`
 * opens the pane, the Top up card lights, and the top bar's primary stands
 * down because the door it opens is already open — exactly what the artboards
 * draw.
 *
 * THE BALANCE IS MONEY, AND SO IS EVERY FIGURE UNDER IT. `GET /me/wallet`
 * gives the balance and the last 25 movements, and that ledger is the ONLY
 * burn rate the backend records — so the runway, the daily rate and "spent
 * this month" are computed from it and say so when there is nothing in it. A
 * balance printed where a runway belongs is a different statement, and so is
 * an invented week count (§4.9).
 *
 * THE PRICES ARE QUOTED, NOT TYPED. See ./useWallet.ts.
 */

const MINOR = 100;

/** "EGP 391" · "EGP 1.50" — the wallet prints money, never credits. */
function money(minor: number, currency: string, decimals = 0): string {
  const major = minor / MINOR;
  return `${currency} ${major.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/** What the ledger says this calendar month cost, and what came back. */
function thisMonth(w: WalletSummary | null) {
  const period = new Date().toISOString().slice(0, 7);
  const rows = (w?.ledger ?? []).filter((l) => (l.createdAt ?? '').slice(0, 7) === period);
  const spentMinor = rows.filter((l) => l.deltaMinor < 0)
    .reduce((s, l) => s + Math.abs(l.deltaMinor), 0);
  const returned = rows.filter((l) => l.entryType === 'refund');
  const returnedMinor = returned.reduce((s, l) => s + l.deltaMinor, 0);
  return { spentMinor, returnedMinor, returnedCount: returned.length, movements: rows.length };
}

/** The daily rate the runway is read from, or null when nothing was spent. */
function dailyMinor(w: WalletSummary | null): number | null {
  const debits = (w?.ledger ?? []).filter((l) => l.deltaMinor < 0);
  if (!debits.length) return null;
  const oldest = debits.reduce((a, b) => (a.createdAt < b.createdAt ? a : b));
  const days = Math.max(1, (Date.now() - new Date(oldest.createdAt).getTime()) / 86_400_000);
  const spent = debits.reduce((s, l) => s + Math.abs(l.deltaMinor), 0);
  return spent / days;
}

type Door = 'topup' | 'auto' | 'messages' | 'history' | 'access';

interface DoorDef {
  key: Door;
  icon: 'plus' | 'refresh' | 'message' | 'history' | 'shield-lock';
  title: string;
  /** The board's caption, when it is not a live figure. */
  sub: string;
  /** False while the board behind it is not this batch's to build. */
  built: boolean;
}

const DOORS: DoorDef[] = [
  { key: 'topup', icon: 'plus', title: 'Top up', sub: '', built: true },
  { key: 'auto', icon: 'refresh', title: 'Auto top-up', sub: '', built: true },
  /* THE DOOR COMES BACK. It was taken off when the board it opens was
     unbuilt; g_wmsgs exists now, so the clinic can reach what spends its
     wallet from the screen that shows the balance. */
  { key: 'messages', icon: 'message', title: 'Patient messages', sub: 'what sends one, and what it costs', built: true },
  { key: 'history', icon: 'history', title: 'History', sub: 'every movement, count × rate', built: true },
  { key: 'access', icon: 'shield-lock', title: 'Access', sub: 'who sees the balance', built: true },
];

/** The board's four amounts. "Other" opens a real field. */
const AMOUNTS = [250, 500, 1000];

export function ManagerWalletPage() {
  const go = useNavigate();
  const [params, setParams] = useSearchParams();
  const door = params.get('door') as Door | null;
  const { wallet, isLoading } = useWallet();
  const { prices } = useMessagePrices(wallet?.currency ?? null);
  const { providers } = usePaymentProviders();
  const topUp = useTopUp();
  const tenantName = useAuthStore((s) => s.activeTenant?.displayName ?? null);

  const [amountMinor, setAmountMinor] = useState(500 * MINOR);
  const [method, setMethod] = useState<string>('card');
  const [typing, setTyping] = useState(false);
  const [query, setQuery] = useState('');
  /* What the Ledger door is showing, so the bar's Export writes that file. */
  const ledgerRows = useRef<string[][]>([]);

  usePageMeta({ title: 'Message wallet · System Settings' });

  const narrow = useNarrowViewport();
  const currency = wallet?.currency?.toUpperCase() ?? 'EGP';
  /* AN EMPTY WALLET IS AN OPERATING STATE (f29), not an error: messages stop
     and the desk gets a call list. The hub says so instead of drawing a
     balance card with a zero in it. */
  const empty = !isLoading && (wallet?.balanceMinor ?? 0) <= 0;
  const weeks = walletRunwayWeeks(wallet);
  const perDay = dailyMinor(wallet);
  const month = useMemo(() => thisMonth(wallet), [wallet]);
  /* The Ledger door reads a calendar month — this one, until the board's
     month picker exists. */
  const currentPeriod = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const [pickedPeriod, setPickedPeriod] = useState<string | null>(null);
  const thisPeriod = pickedPeriod ?? currentPeriod;
  /* The month picker's choices: this month and the five before it. */
  const periodChoices = useMemo(() => {
    const [y, m] = currentPeriod.split('-').map(Number);
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(y, m - 1 - i, 1);
      return {
        value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleDateString('en-GB', { month: 'long' }),
      };
    });
  }, [currentPeriod]);
  const monthLabel = useMemo(
    () => new Date(`${thisPeriod}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'long' }),
    [thisPeriod],
  );

  /* Stripe is the only provider that can be charged on this deployment;
     Paymob is registered and unavailable. So the board's four methods are
     drawn, and the three this deployment cannot take are marked rather than
     offered and refused at the end (§4.8). */
  const stripeLive = providers.some((p) => p.id === 'stripe' && p.available);
  const paymobLive = providers.some((p) => p.id === 'paymob' && p.available);
  const METHODS: { key: string; label: string; live: boolean }[] = [
    { key: 'instapay', label: 'InstaPay', live: paymobLive },
    { key: 'fawry', label: 'Fawry', live: paymobLive },
    { key: 'transfer', label: 'Bank transfer', live: false },
    { key: 'card', label: 'Card', live: stripeLive },
  ];

  const setDoor = (next: Door | null) => {
    const p = new URLSearchParams(params);
    if (next) p.set('door', next); else p.delete('door');
    setParams(p, { replace: true });
  };

  const openDoor = (d: DoorDef) => {
    /* Patient messages is a screen of its own, not a door docked on the hub:
       it carries six switches, a pane and its own Save. */
    if (d.key === 'messages') { go('/clinic-manager/settings/messages'); return; }
    if (!d.built) return;
    setDoor(d.key);
  };

  /** "about 5 weeks" for a top-up amount, from the clinic's own burn rate. */
  const runwayFor = (minor: number): string | null => {
    if (!perDay || perDay <= 0) return null;
    const days = minor / perDay;
    if (days >= 60) return `about ${Math.round(days / 30)} months`;
    return `about ${Math.max(1, Math.round(days / 7))} weeks`;
  };

  const q = query.trim().toLowerCase();
  const matches = (text: string) => !q || text.toLowerCase().includes(q);

  const topUpPane = (
    <Pane
      head={(
        <PaneHead overline="The Top up door" title="Add money to the wallet" sub="no packs, no bonus balance" />
      )}
      foot={(
        <button type="button" className="btn btn-primary" style={{ flex: 1 }}
          disabled={topUp.isPending}
          onClick={() => topUp.mutate(amountMinor)}>
          {topUp.isPending ? 'Paying…' : `Pay ${money(amountMinor, currency)}`}
        </button>
      )}
    >
      <>
        {AMOUNTS.map((a) => {
          const minor = a * MINOR;
          const on = minor === amountMinor;
          const runway = runwayFor(minor);
          return (
            <Card key={a}
              style={on
                ? { borderColor: 'var(--brand-500)', background: 'var(--brand-100)', cursor: 'pointer' }
                : { cursor: 'pointer' }}
              onClick={() => setAmountMinor(minor)}>
              <div className="flex" style={{ gap: 10 }}>
                <div className="grow">
                  <p className="t-title">{money(minor, currency)}</p>
                  {/* The board's "about 2 weeks of messages" is this clinic's
                      own burn rate applied to the amount. With nothing spent
                      yet there is no rate, and the line says so instead of
                      converting money into a week count nobody measured. */}
                  <p className="t-cap mut">{runway ?? 'no rate yet — nothing has been spent'}</p>
                </div>
                {on ? <Chip kind="brand" small>✓</Chip> : null}
              </div>
            </Card>
          );
        })}
        <Card style={{ cursor: 'pointer' }} onClick={() => setTyping(true)}>
          <div className="flex" style={{ gap: 10 }}>
            <div className="grow">
              <p className="t-title">Other amount</p>
              <p className="t-cap mut">type any figure</p>
            </div>
          </div>
        </Card>

        <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>Pay with</p>
        {/* `cm-chiprow` returns an actionable chip to the canvas's own 24px
            box — the kit gives `.chip-act` a 40px min-height for touch, which
            is right on a phone and eight pixels too tall here (manager.css §6). */}
        <div className="cm-chiprow" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {METHODS.filter((m) => m.live).map((m) => (
            /* Only the methods this deployment can actually charge — a chip
               that cannot be picked is a dead control. An unavailable chip is
               never drawn, not disabled-
               looking: it simply does not select. */
            <Chip key={m.key}
              kind={m.key === method ? 'brand' : 'outline'} small
              className={m.key === method ? 'w6' : undefined}
              onClick={m.live ? () => setMethod(m.key) : undefined}>
              {m.label}
            </Chip>
          ))}
        </div>

        <Card>
          <p className="t-ovl mut">A tax invoice is issued</p>
          <p className="t-sec sec" style={{ marginTop: 4 }}>{tenantName || 'This clinic'}</p>
          <p className="t-cap mut">
            the accountant books it under Expenses ▸ Subscriptions
          </p>
        </Card>
        <Card kind="ghost">
          <p className="t-sec mut">
            {METHODS.some((m) => m.live)
              ? 'A card clears at once; a wallet payment lands within minutes'
              : 'Payments are not switched on for this clinic — MAIE support turns them on'}
          </p>
        </Card>
        {topUp.isError ? (
          <p className="t-sec red" style={{ marginTop: 8 }}>
            {(topUp.error as { response?: { data?: { error?: { message?: string } } } })
              ?.response?.data?.error?.message
              ?? 'That top-up did not go through. Nothing was charged and nothing was credited.'}
          </p>
        ) : null}
        {topUp.isSuccess ? (
          <p className="t-sec grn" style={{ marginTop: 8 }}>Paid — the balance above has moved.</p>
        ) : null}
      </>
    </Pane>
  );

  const crumb = usePhoneCrumb('Settings', door === 'access' ? 'Wallet · Access' : door === 'history' ? 'Wallet · Ledger' : door === 'topup' ? 'Wallet · Top up' : door === 'auto' ? 'Wallet' : 'Message wallet', { back: door ? '/clinic-manager/settings/wallet' : undefined });
  useShellChrome(
    {
      raw: true,
      ...crumb,
      phoneMenu: (close) => <ManagerMenu door="settings" onClose={close} />,
      paneOpen: door === 'topup' || door === 'auto',
      onPaneClose: () => setDoor(null),
      paneTitle: door === 'auto' ? 'Auto top-up' : 'Top up',
      title: door === 'access' ? 'Access' : door === 'history' ? 'Ledger' : 'Message wallet',
      sub: door === 'access'
        ? 'two verbs, five roles'
        : door === 'history'
        ? `${monthLabel} · all branches`
        : empty && !door
          ? 'empty · messages are paused'
        : door === 'topup'
          ? 'the Top up door, docked on the hub'
          : door === 'auto'
            ? 'the Auto top-up door, docked on the hub'
          : `what the clinic’s outgoing messages spend · in ${currency}`,
      search: door === 'access'
        ? 'Search a role…'
        : door === 'history'
          ? 'Search a movement, an invoice…'
          : 'Search the wallet — a movement, an invoice, a switch…',
      searchValue: query,
      onSearchChange: setQuery,
      subrail: <SettingsRail active="wallet" />,
      /* The primary stands down while the door it opens is open — a button
         that re-opens what is already open is a control with nothing to do. */
      primary: door === 'topup' || door === 'access' || door === 'auto'
        ? undefined
        : door === 'history'
          ? {
              label: 'Export',
              onClick: () =>
                exportCsv(
                  `wallet-${thisPeriod}.csv`,
                  ['Movement', 'Count × rate', `Amount (${currency})`],
                  ledgerRows.current,
                ),
            }
          : { label: 'Top up', onClick: () => setDoor('topup') },
      pane: door === 'topup' ? topUpPane : door === 'auto' ? <AutoTopUpPane onDone={() => setDoor(null)} /> : undefined,
    },
    [
      query, door ?? '', thisPeriod, wallet?.autoReload.enabled ?? false, wallet?.autoReload.cardOnFile ?? false, currency, amountMinor, method,
      topUp.isPending, topUp.isError, topUp.isSuccess,
      /* `perDay` is read off Date.now(), so it moves every render; as a dependency it
         re-ran this effect forever. Whole minor units is as exact as the pane needs. */
      Math.round(perDay ?? -1), wallet?.balanceMinor ?? -1, providers.length,
    ],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <FilterRow>
        <p className="t-sec mut">
          <button type="button" className="w6 brand"
            style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={() => go('/clinic-manager/settings')}>
            ‹ Settings
          </button>
          {' '}<span className="mut">▸</span>{' '}
          {door === 'topup' || door === 'access' || door === 'history' || door === 'auto' ? (
            <>
              <button type="button" className="w6 brand"
                style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
                onClick={() => setDoor(null)}>
                Message wallet
              </button>
              {' '}<span className="mut">▸</span>{' '}
              <span className="t-sec sec w6">
                {door === 'access' ? 'Access' : door === 'history' ? 'Ledger' : door === 'auto' ? 'Auto top-up' : 'Top up'}
              </span>
            </>
          ) : 'Message wallet'}
        </p>
      </FilterRow>
      <div className="dcontent">
        {/* Access (f29b) is a screen of its own — its own title, its own
            breadcrumb — not a pane docked on the hub. */}
        {empty && !door ? <WalletEmpty wallet={wallet ?? null} onTopUp={() => setDoor('topup')} onAuto={() => setDoor('auto')} /> :
         door === 'history' ? (
          <>
          {narrow ? (
            <div style={{ margin: '14px 0 10px' }}>
              <select aria-label="Month" className="cm-inp" value={thisPeriod}
                onChange={(e) => setPickedPeriod(e.target.value)}
                style={{ width: 'auto', borderRadius: 999, padding: '8px 14px', fontWeight: 600,
                  background: 'var(--brand-100)', color: 'var(--brand-700, var(--brand-600))' }}>
                {periodChoices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          ) : null}
          <WalletLedger
            period={thisPeriod}
            currency={currency}
            onRows={(r) => { ledgerRows.current = r; }}
          />
          </>
        ) :
         door === 'access' ? <WalletAccess query={query} /> : (
        <>
        <div className="cm-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ flex: 1.2, minWidth: 0 }}>
            <Card kind="accent" style={{ background: 'var(--brand-100)' }}>
              <p className="t-ovl mut">Balance</p>
              <p className="t-dxl" style={{ margin: '4px 0' }}>
                {isLoading && !wallet ? '—' : money(wallet?.balanceMinor ?? 0, currency)}
              </p>
              <p className="t-sec sec">
                {weeks !== null && perDay
                  ? `about ${weeks} weeks at your rate · ${money(Math.round(perDay), currency, 2)} a day`
                  : 'no messages have been sent from this wallet yet — there is no rate to read'}
              </p>
              <p className="t-cap mut mt8">
                {month.movements
                  ? (
                    <>
                      spent this month {money(month.spentMinor, currency)} ·{' '}
                      {month.returnedCount} undelivered, {money(month.returnedMinor, currency)} returned
                      {' — '}
                      <a href="#ledger" className="brand w6"
                        onClick={(e) => { e.preventDefault(); setDoor('history'); }}>
                        the {new Date().toLocaleString('en-GB', { month: 'long' })} ledger
                      </a>
                    </>
                  )
                  : 'nothing spent this month · no movements on record'}
              </p>
            </Card>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Card>
              <p className="t-ovl mut mb8">What things cost</p>
              <div className="flex" style={{ gap: 12, marginTop: 6 }}>
                <p className="t-body grow">One clinic message</p>
                <p className="t-strong">
                  {prices ? money(prices.whatsappMinor, currency, 2) : <span className="mut">—</span>}
                </p>
              </div>
              <div className="flex" style={{ gap: 12, marginTop: 6 }}>
                <p className="t-body grow">SMS, when there is no WhatsApp</p>
                <p className="t-strong">
                  {prices ? money(prices.smsMinor, currency, 2) : <span className="mut">—</span>}
                </p>
              </div>
              <div className="flex" style={{ gap: 12, marginTop: 6 }}>
                <p className="t-body grow">A patient replying to you</p>
                <p className="t-strong grn">free</p>
              </div>
              <p className="t-cap mut mt8">
                One printed price list, three lines. The largest by volume — the reply — is the
                free one.
              </p>
            </Card>
          </div>
        </div>

        <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>The wallet</p>
        <div className="cm-wallet-doors">
          {DOORS.filter((d) => matches(`${d.title} ${d.sub}`)).map((d) => {
            const open = door === d.key;
            const caption = d.key === 'topup'
              ? METHODS.filter((m) => m.live).map((m) => m.label).join(' · ') || 'payments not switched on'
              : d.key === 'auto'
                ? (wallet?.autoReload.enabled
                  ? `on · below ${money(wallet.autoReload.thresholdMinor ?? 0, currency)} add ${money(wallet.autoReload.reloadAmountMinor ?? 0, currency)}`
                  : 'off')
                : d.sub;
            return (
              <Card key={d.key}
                style={open
                  ? { borderColor: 'var(--brand-500)', background: 'var(--brand-100)' }
                  : { cursor: 'pointer' }}
                onClick={() => openDoor(d)}>
                <div className="flex" style={{ gap: 12 }}>
                  <span className="brand"><Icon name={d.icon} size="lg" /></span>
                  <div className="grow">
                    <p className="t-strong">
                      {d.title}
                    </p>
                    <p className="t-cap mut">{caption}</p>
                  </div>
                  {open
                    ? <Chip kind="brand" small>open</Chip>
                    : <span className="mut"><Icon name="chevron-right" /></span>}
                </div>
              </Card>
            );
          })}
        </div>

        <Card kind="accent">
          <p className="t-sec brand">
            The wallet holds POUNDS, not credits: what you pay is what the balance shows, and it
            never expires. A second currency nobody can check is how a clinic stops trusting the
            number.
          </p>
        </Card>
        </>
        )}
      </div>

      <TextEditor
        open={typing}
        title="Other amount"
        sub={`how much to add to the wallet, in ${currency}`}
        value={String(amountMinor / MINOR)}
        onClose={() => setTyping(false)}
        onDone={(next) => {
          const n = Number(next);
          if (Number.isFinite(n) && n > 0) setAmountMinor(Math.round(n * MINOR));
        }}
      />

    </div>
  );
}
